import { action, computed, makeObservable } from 'mobx';

import { ValueModel } from '@/models/ValueModel';
import { Breakpoint } from '@/styles/breakpoints';

import { IUiStore } from './types';

class UiStore implements IUiStore {
  private screenSizeModel = new ValueModel<Breakpoint>(Breakpoint.desktop);

  constructor() {
    makeObservable(this, {
      updateScreenSize: action,
      screenSize: computed,
      isDesktop: computed,
      isLaptop: computed,
      isLaptopLarge: computed,
      isLaptopMedium: computed,
      isTablet: computed,
      isMobile: computed,
      isMobileAndLower: computed,
      isDesktopAndLower: computed,
      isLaptopLargeAndLower: computed,
      isLaptopMediumAndLower: computed,
      isTabletAndLower: computed,
      isTabletSAndLower: computed,
    });
  }

  get screenSize(): Breakpoint {
    return this.screenSizeModel.value;
  }

  /** screenSize >= 1440px */
  get isDesktop(): boolean {
    return this.screenSize === Breakpoint.desktop;
  }

  /** screenSize <= 1440px */
  get isDesktopAndLower(): boolean {
    return this.screenSize <= Breakpoint.desktop;
  }

  /** 1024px - 1439px */
  get isLaptop(): boolean {
    return this.screenSize === Breakpoint.laptopL || this.screenSize === Breakpoint.laptopM;
  }
  get isLaptopMedium(): boolean {
    return this.screenSize === Breakpoint.laptopM;
  }
  get isLaptopLarge(): boolean {
    return this.screenSize === Breakpoint.laptopL;
  }

  /** screenSize <= 1280px */
  get isLaptopLargeAndLower(): boolean {
    return this.screenSize <= Breakpoint.laptopL;
  }

  get isLaptopMediumAndLower(): boolean {
    return this.screenSize <= Breakpoint.laptopM;
  }

  /** 680px - 1023px */
  get isTablet(): boolean {
    return this.screenSize === Breakpoint.tabletS || this.screenSize === Breakpoint.tabletM;
  }

  /** screenSize <= 1023px */
  get isTabletAndLower(): boolean {
    return this.screenSize <= Breakpoint.tabletM;
  }

  /** screenSize <= 767 */
  get isTabletSAndLower(): boolean {
    return this.screenSize <= Breakpoint.tabletS;
  }

  /** 425px - 679px */
  get isMobile(): boolean {
    return this.screenSize === Breakpoint.mobile;
  }

  /** screenSize <= 680px */
  get isMobileAndLower(): boolean {
    return this.screenSize <= Breakpoint.mobile;
  }

  /** 0px - 424px */
  get isXs(): boolean {
    return this.screenSize === Breakpoint.xs;
  }

  updateScreenSize = () => {
    this.screenSizeModel.change(this.calculateScreenSize());
  };

  private calculateScreenSize = (): Breakpoint => {
    const currentWidth = window.innerWidth;

    if (currentWidth >= Breakpoint.desktop) {
      return Breakpoint.desktop;
    }

    if (currentWidth >= Breakpoint.laptopL) {
      return Breakpoint.laptopL;
    }

    if (currentWidth >= Breakpoint.laptopM) {
      return Breakpoint.laptopM;
    }

    if (currentWidth >= Breakpoint.tabletM) {
      return Breakpoint.tabletM;
    }

    if (currentWidth >= Breakpoint.tabletS) {
      return Breakpoint.tabletS;
    }

    if (currentWidth >= Breakpoint.mobile) {
      return Breakpoint.mobile;
    }

    return Breakpoint.xs;
  };
}

export const uiStore = new UiStore();
